import { useMutation } from '@apollo/client';
import { Button, Divider, Form, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import { ClockInRestrictionType, EquipmentSelectionOptions, JobCostingPromptOptions } from '__generated__/graphql';
import { POSITION_CREATE_MUTATION } from 'apollo/mutations/position-mutations';
import classNames from 'classnames';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import { useToastDispatch } from 'contexts/ToastContext';
import { useDefaultTimes, useOpenable, useOrganization } from 'hooks';
import useClockInRestrictionTimeConverter from 'hooks/models/position/useClockInRestrictionTimeConverter';
import { DateTime } from 'luxon';
import { useRef } from 'react';
import { ClassName } from 'types/ClassName';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import {
  IPhotoVerificationSettings,
  PositionManageBudgetType,
  PositionPhotoVerificationSettingsActionType,
  convertPhotoVerificationSettingsToString,
  defaultPositionPPESettings,
} from 'utils/positionUtils';
import { PhotoVerificationOptionType } from '../../util/photoVerificationSettingsFormUtils';
import CreatePositionCompanyManagementSection from './CreatePositionCompanyManagementSection/CreatePositionCompanyManagementSection';
import CreatePositionGeneralSection from './CreatePositionGeneralSection/CreatePositionGeneralSection';
import CreatePositionOtherSection from './CreatePositionOtherSection/CreatePositionOtherSection';
import CreatePositionSafetySection from './CreatePositionSafetySection/CreatePositionSafetySection';
import CreatePositionSupervisionSection from './CreatePositionSupervisionSection/CreatePositionSupervisionSection';

export interface ICreatePositionFormProps {
  level: number;
  onSubmit: (data: ICreatePositionsFormData) => void;
  className?: ClassName;
}

export interface ICreatePositionsFormData {
  title: string;
  level: number;
  manageProjects: boolean;
  manageCostCodes: boolean;
  viewExpenses: boolean;
  manageMediaCollection: number;
  viewMediaCollection: number;
  memberLock: number;
  manageCompanySettings: boolean;
  manageEmployees: number;
  manageEmployeeGroups: boolean;
  timeEvents: number;
  manageTimeEntries: number;
  manageEquipment: boolean;
  manageBudgets: PositionManageBudgetType;
  viewMemberActivityReport: number;
  viewAllActivityReport: string;
  scopeByGroup: boolean;
  dailyProjectPrompts: boolean;
  dailyProjectPromptsUseSubprojects: boolean;
  manageSchedule: number;
  viewSchedule: number;
  clockInRestriction: ClockInRestrictionType;
  clockInRestrictionStartTime: DateTime;
  canTrackProgress: boolean;
  canManageProjectCostCodeQuantity: number;
  photoVerificationSettings: string;
  photoVerificationOptionType: PhotoVerificationOptionType;
  jobCostingPrompt: JobCostingPromptOptions;
  manageBulletinPosts: boolean;
  equipmentSelectionRequirement: EquipmentSelectionOptions;
  canRequestTimeOff: boolean;
  manageGpsApproval: number;
  managePerDiem: number;
  createIncident: boolean;
  manageIncident: boolean;
  performInspections: boolean;
  manageInspections: boolean;
  reportObservations: boolean;
  manageObservations: boolean;
  performTraining: boolean;
  manageTraining: boolean;
  useKiosk: boolean;
  createSafetyAnalysisReports: boolean;
  manageSafetyAnalysisReports: boolean;
  manageCertificates: boolean;
}

const CreatePositionsForm = ({ level, onSubmit, className }: ICreatePositionFormProps) => {
  const { defaultStartTime } = useDefaultTimes();
  const formData = useRef<ICreatePositionsFormData>({
    title: '',
    level,
    manageProjects: false,
    manageCostCodes: false,
    viewExpenses: false,
    manageMediaCollection: 0,
    viewMediaCollection: 1,
    memberLock: 0,
    manageCompanySettings: false,
    manageEmployees: 0,
    manageEmployeeGroups: false,
    timeEvents: 1,
    manageTimeEntries: 0,
    manageEquipment: false,
    manageBulletinPosts: false,
    manageBudgets: PositionManageBudgetType.OFF,
    viewMemberActivityReport: 1,
    viewAllActivityReport: '0', // String representation of a bool
    scopeByGroup: false,
    dailyProjectPrompts: false,
    dailyProjectPromptsUseSubprojects: false,
    clockInRestrictionStartTime: defaultStartTime,
    clockInRestriction: ClockInRestrictionType.Off,
    viewSchedule: 1,
    manageSchedule: 1,
    canTrackProgress: false,
    canManageProjectCostCodeQuantity: 0,
    photoVerificationSettings: convertPhotoVerificationSettingsToString({
      enabled: false,
      actions: [
        PositionPhotoVerificationSettingsActionType.CLOCK_IN,
        PositionPhotoVerificationSettingsActionType.CLOCK_OUT,
        PositionPhotoVerificationSettingsActionType.START_BREAK,
        PositionPhotoVerificationSettingsActionType.END_BREAK,
      ],
      ppe: defaultPositionPPESettings(),
    }),
    photoVerificationOptionType: PhotoVerificationOptionType.KIOSK_ONLY,
    jobCostingPrompt: JobCostingPromptOptions.Off,
    equipmentSelectionRequirement: EquipmentSelectionOptions.Off,
    canRequestTimeOff: true,
    manageGpsApproval: 0,
    managePerDiem: 0,
    createIncident: false,
    manageIncident: false,
    performInspections: false,
    manageInspections: false,
    reportObservations: false,
    manageObservations: false,
    performTraining: false,
    manageTraining: false,
    useKiosk: true,
    createSafetyAnalysisReports: false,
    manageSafetyAnalysisReports: false,
    manageCertificates: false,
  });

  const organization = useOrganization();
  const toastDispatch = useToastDispatch();
  const [createPosition] = useMutation(POSITION_CREATE_MUTATION);
  const { toClockInRestriction } = useClockInRestrictionTimeConverter();
  const isPPEEnabled = useFeatureFlags('PHOTO_VERIFICATION_PPE');
  const scansDialog = useOpenable();
  const classes = classNames('create-position-form', className);

  function renderFormFields(form: IFormRender<ICreatePositionsFormData>) {
    return (
      <>
        <CreatePositionGeneralSection form={form} />
        <Divider className="my-6" />
        <CreatePositionCompanyManagementSection form={form} />
        <Divider className="my-6" />
        <CreatePositionSafetySection form={form} />
        <Divider className="my-6" />
        <CreatePositionSupervisionSection form={form} />
        <Divider className="my-6" />
        <CreatePositionOtherSection form={form} />
        <Button className="my-4" type="primary" onClick={form.handleSubmit}>
          {t('Create')}
        </Button>
      </>
    );
  }

  async function onFormSubmit(data: ICreatePositionsFormData) {
    if (isPPEEnabled && data.photoVerificationSettings) {
      const settings: IPhotoVerificationSettings = JSON.parse(data.photoVerificationSettings);
      if (settings.enabled && settings.ppe && settings.ppe.some((ppe) => ppe.scans.length <= 0)) {
        scansDialog.open();
        return;
      }
    }

    try {
      const viewAllActivityReport = data.viewAllActivityReport === '1';

      const newData = { ...data, canTrackProgress: undefined, photoVerificationOptionType: undefined }; // canTrackProgress & photoVerificationOptionType are not fields on PositionCreateInput

      let updatedValue = {
        manageBudgets: false,
        viewBudgets: false,
      };

      switch (data.manageBudgets) {
        case PositionManageBudgetType.MANAGE:
          updatedValue = {
            manageBudgets: true,
            viewBudgets: true,
          };
          break;
        case PositionManageBudgetType.VIEW_ONLY:
          updatedValue = {
            manageBudgets: false,
            viewBudgets: true,
          };
          break;
        case PositionManageBudgetType.OFF:
          updatedValue = {
            manageBudgets: false,
            viewBudgets: false,
          };
          break;
      }

      await createPosition({
        variables: {
          position: {
            ...newData,
            ...updatedValue,
            viewAllActivityReport,
            viewProjectExpenses: data.viewExpenses,
            viewMemberExpenses: data.viewExpenses,
            viewExpenses: undefined,
            organizationId: organization.id,
            viewEquipmentStatus: data.manageEquipment,
            viewEquipmentDetails: data.manageEquipment,
            viewMemberActivityReport: !viewAllActivityReport ? data.viewMemberActivityReport : 15,
            manageProjectGroups: data.manageProjects,
            requestSignatures: 0,
            clockInRestrictionStartTime: toClockInRestriction(data.clockInRestrictionStartTime),
            canRequestTimeOff: data.canRequestTimeOff,
            manageGpsApproval: data.manageGpsApproval,
            createdOn: isoTimeStampUtc(),
          },
        },
      });
    } catch (e) {
      toastDispatch({
        type: 'OPEN',
        payload: {
          theme: Theme.DANGER,
          label: t('Unable to create position. Please try again later.'),
        },
      });
    }

    onSubmit(data);
  }

  return (
    <>
      <Form<ICreatePositionsFormData>
        render={renderFormFields}
        onSubmit={onFormSubmit}
        className={classes}
        data={formData.current}
        allowMultipleSubmissions={true}
      />
      <ConfirmationDialog
        isOpen={scansDialog.isOpen}
        title={t('No Scan Items Selected')}
        message={t('In order to have a PPE Scan enabled, you must select at least one PPE Scan Item.')}
        closeButtonText={t('Close')}
        onClose={scansDialog.close}
        className={classes}
      />
    </>
  );
};

export default CreatePositionsForm;
